import type RpcDataSort     from "@/core/services/Rpc/RpcDataSort";
import Core                 from '@/core/Core';
import ProductCategoriesDAO from './ProductCategoriesDAO';
import ProductCategory      from './ProductCategory';
import RpcDataPagination    from "@/core/services/Rpc/RpcDataPagination";

class ProductCategoriesService {

	static productCategoriesMap: Map<any, ProductCategory> | null = null;

  /** Поиск категорий с пагинацией под поисковую выдачу */
  static async search(
    params: Parameters<typeof ProductCategoriesDAO.getList>[0]
  ): Promise<ProductCategory[]> {
    const result = await this.getListPaginated({
      ...params,
      pagination: null, // RpcDataPagination.createForSearch() - нельзя использовать вместе с preloadOnMount и с preloadOnFocus, тк эти прелоады запрещают отправку запросов с паттерном и мы будем видеть статичный массив из 8 записей
    })

    return result.data;
  }

  static async getListPaginated(
    params?: Parameters<typeof ProductCategoriesDAO.getList>[0] & {
      pagination?: RpcDataPagination;
      sort?: RpcDataSort;
    }
  ): Promise<{ data: ProductCategory[]; pagination: RpcDataPagination }> {
    const { pagination, sort, ...filters } = params ?? {};
    const response = await ProductCategoriesDAO.getList(filters, pagination, sort);

    return {
      data:       Core.utils.hydrateArray(response.data, ProductCategory.fromServer),
      pagination: response.meta.pagination,
    };
  }

	static async getList(
    params: Parameters<typeof ProductCategoriesDAO.getList>[0]
  ): Promise<ProductCategory[]> {
		const response = await ProductCategoriesDAO.getList(params);

		return Core.utils.hydrateArray(response.data, ProductCategory.fromServer);
	}

	static async get(id: number | string, options?: {
		requireAllowedUnits?: boolean;
		requireCharacteristics?: boolean;
	}): Promise<ProductCategory | null> {
		const response = await ProductCategoriesDAO.get(id, options);

		if (response.ok) {
			return ProductCategory.fromServer(response.data);
		}

		return null;
	}

	static async add(category: ProductCategory): Promise<any | null> {
		const response = await ProductCategoriesDAO.add(category);

		if (response.ok) {
			Core.showNotification.success('Успешно создано');
			return ProductCategory.fromServer(response.data);
		}

		return null;
	}

	static async update(category: ProductCategory): Promise<boolean> {
		const response = await ProductCategoriesDAO.update(category);

		if (response.ok) {
			Core.showNotification.success('Успешно обновлено');
		}

		return response.ok;
	}

	static async delete(id: number): Promise<boolean> {
		const response = await ProductCategoriesDAO.delete(id);

		if (response.ok) {
			Core.showNotification.success('Успешно удалено');
		}

		return response.ok;
	}

	static async getPath(productCategoryId: number): Promise<any> {
		const response = await ProductCategoriesDAO.getPath(productCategoryId);

		if (response.ok) {
			return response.data;
		}

		return null;
	}

	/**
	 * Корневая категория для указанной. Путь отдаёт бэк, корнем вперёд;
	 * сам корень догружаем целиком — нужны его реквизиты, например основание платежа.
	 */
	static async getRootCategory(categoryId: number): Promise<ProductCategory | null> {
		const path = await this.getPath(categoryId);

		if (Core.utils.isNotEmptyArray(path) === false) {
			return null;
		}
		else {
			return this.get(path[0].id);
		}
	}

	/**
	 * Загружает полное дерево категорий и строит карту.
	 * Пагинация выключена намеренно: карта строит иерархию, и обрезанный список
	 * молча дал бы категории без родителей.
	 */
	static async load(): Promise<void> {
		const response = await ProductCategoriesDAO.getList(
			{ showUnpublished: true },
			RpcDataPagination.createDisabled()
		);

		if (!response.ok) {
			this.productCategoriesMap = null;
			throw new Error('Не удалось загрузить категории товаров');
		}

		const map = new Map();

		Core.utils.hydrateArray(response.data, ProductCategory.fromServer).forEach(category => {
			if (!category.isLeaf) category.children = [];
			map.set(category.id, category);
		});

		map.forEach(category => {
			if (category.parentId && map.get(category.parentId)) {
				map.get(category.parentId).children.push(category.id);
				// используем _parent, а не parent (чтобы не вызвать сеттер)
				category._parent = map.get(category.parentId);
			}
		});

		this.productCategoriesMap = map;
	}

	static getProductCategoriesMap(): Map<any, ProductCategory> {
		if (this.productCategoriesMap === null) {
			throw new Error('Карта категорий не построена: ProductCategoriesService.load() ещё не вызывался');
		}

		return this.productCategoriesMap;
	}

	static resetProductCategoriesMap(): void {
		this.productCategoriesMap = null;
	}

	static async attachCharacteristic(
		categoryId: number,
		characteristicId: number
	): Promise<boolean> {
		const response = await ProductCategoriesDAO.attachCharacteristic(categoryId, characteristicId);

		if (response.ok) {
			Core.showNotification.success('Успешно добавлено');
		}

		return response.ok;
	}

	static async detachCharacteristic(
		categoryId: number,
		characteristicId: number
	): Promise<boolean> {
		const response = await ProductCategoriesDAO.detachCharacteristic(categoryId, characteristicId);

		if (response.ok) {
			Core.showNotification.success('Успешно удалено');
		}

		return response.ok;
	}

	static async updateIsRequiredCharacteristic(
		productCategoryId: number,
		characteristicId: number,
		isRequired: boolean
	): Promise<boolean> {
		const response = await ProductCategoriesDAO.updateCharacteristicRequired(
			productCategoryId,
			characteristicId,
			isRequired
		);

		if (response.ok) {
			Core.showNotification.success('Успешно обновлено');
		}

		return response.ok;
	}

	static async attachAllowedUnit(
		productCategoryId: number,
		unitId: number
	): Promise<boolean> {
		const response = await ProductCategoriesDAO.attachAllowedUnit(productCategoryId, unitId);

		if (response.ok) {
			Core.showNotification.success('Успешно добавлено');
		}

		return response.ok;
	}

	static async detachAllowedUnit(
		productCategoryId: number,
		unitId: number
	): Promise<boolean> {
		const response = await ProductCategoriesDAO.detachAllowedUnit(productCategoryId, unitId);

		if (response.ok) {
			Core.showNotification.success('Успешно удалено');
		}

		return response.ok;
	}

	/**
	 * Причина, по которой у категории нельзя отвязать единицу измерения. null — можно.
	 * У опубликованной категории должна остаться хотя бы одна единица.
	 */
	static getReasonCannotDetachUnit(productCategory: ProductCategory): string | null {
		if (productCategory.isPublished === true && productCategory.allowedUnits.length < 2) {
			return 'Нельзя удалить все единицы измерения у опубликованного товара';
		}
		else {
			return null;
		}
	}

	/** Причина, по которой к категории нельзя привязать товар. null — можно. */
	static getReasonCannotAttachProduct(productCategory: ProductCategory): string | null {
		if (productCategory.allowsProducts === false) {
			return 'Категория не допускает товары — у неё не заданы НДС, отсрочка и единицы измерения';
		}
		else {
			return null;
		}
	}

	/**
	 * Диалог выбора единиц измерения. Привязку на бэке и правку
	 * productCategory.allowedUnits делает сам диалог.
	 */
	static showProductCategoryAllowedUnitsDialog(productCategory: ProductCategory): Promise<boolean> {
		return Core.dialogs.ProductCategoryAllowedUnitsDialog.showAsPromise({ productCategory });
	}

	/**
	 * Диалог выбора характеристик. Привязку на бэке и правку
	 * productCategory.characteristics делает сам диалог.
	 */
	static showProductCategoryCharacteristicsDialog(productCategory: ProductCategory): Promise<boolean> {
		return Core.dialogs.ProductCategoryCharacteristicsDialog.showAsPromise({ productCategory });
	}
}

export default ProductCategoriesService;